import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CompassIcon, TargetIcon, ShieldIcon, CheckCircle2Icon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { SectionHeading } from '../components/ui/SectionHeading';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
export function About() {
  return <>
      {/* Hero */}
      <section id="who-we-are" className="relative overflow-hidden" data-id="element-804" data-mp="326gegca4stuf">
        <GeometricAccent variant="dots" className="absolute top-10 left-6 w-32 h-32" opacity={0.4} data-id="element-805" data-mp="376vpgx6xxp98" />
        <Container className="relative pt-8 md:pt-12 pb-8 md:pb-12" data-id="element-806" data-mp="2x97qtw67rxls">
          <div className="grid lg:grid-cols-12 gap-10 items-start" data-id="element-807" data-mp="2x7d8mpmbujle">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-808" data-mp="1gtwput5b5eb4">
              <span className="inline-block text-[11px] font-semibold tracking-[0.18em] text-forest-700 uppercase mb-3" data-id="element-809" data-mp="23izt4shruds8">
                Who We Are
              </span>
              <p className="font-display italic text-2xl text-sand-500 mb-4" data-id="element-810" data-mp="1ylq1d66wtq18">
                بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
              </p>
              <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-ink-900" data-id="element-811" data-mp="28grsu8onr46z">
                International Islamic
                <br data-id="element-812" data-mp="2i7cp3pguzwmq" />
                Learning Hub
              </h1>
              <p className="mt-6 text-ink-700 leading-relaxed" data-id="element-813" data-mp="2i85zr1ixv6a5">
                IILH is a global learning platform designed to support Muslim
                families beyond borders. We work at the intersection of
                education, identity, and integration, addressing the unique
                challenges faced by Muslim children and parents living in
                non-Muslim environments.
              </p>
              <p className="mt-6 text-ink-700 leading-relaxed" data-id="element-814" data-mp="1stvioxdp1ggm">
                Our programs are delivered online and through partner
                institutions, allowing us to reach families across Europe, North
                America, and other regions where Muslim minorities require
                structured, culturally sensitive support.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6 relative" data-id="element-815" data-mp="2cmbontxt19c1">
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-816" data-mp="r8yx7kx57ofv">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/aCgn5fRf4NDJanaSW5LeLk/About-Us-2.jpg" alt="Muslim mother and two children in a live online lesson at home with a Paris rooftop view" data-id="element-817" data-mp="jpg5mq9u2mvo" />
              </div>
            </motion.div>
          </div>

          {/* Full-width supporting copy */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-10 max-w-none" data-id="element-818" data-mp="12kwj6ucddk5z">
            <p className="text-forest-700 font-semibold" data-id="element-819" data-mp="3deisdrj0ip6z">
              We are not a traditional school.
            </p>
            <p className="mt-2 text-ink-700 leading-relaxed" data-id="element-820" data-mp="39bvub8vr081c">
              We are a learning hub — connecting education, families,
              professionals, and communities around a shared mission.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Vision & Mission */}
      <section id="vision-mission" className="relative py-16 md:py-24 overflow-hidden bg-sage-50/60" data-id="element-821" data-mp="r7e2qne46pbo">
        <Container data-id="element-822" data-mp="w2jljdc7znvj">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight mb-10 text-center" data-id="element-823" data-mp="1juqe01etrapo">
            Vision & Mission
          </h2>
          <div className="grid lg:grid-cols-12 gap-12 items-stretch" data-id="element-824" data-mp="1ot1p0fguvr9h">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-5" data-id="element-825" data-mp="1auk6cw5t54yf">
              <div className="aspect-[4/5] lg:aspect-auto lg:h-full rounded-3xl overflow-hidden shadow-card" data-id="element-826" data-mp="3s46jms0rmran">
                <img src="https://cdn.magicpatterns.com/uploads/v3TjHD3rav5saTSn5G19iR/Vision3.jpg" alt="Muslim children collaborating on a model and drawings around a table in a bright modern classroom with a live online class on screen" className="w-full h-full object-cover" data-id="element-827" data-mp="5uvmb054uh79" />
              </div>
            </motion.div>
            <div className="lg:col-span-7" data-id="element-828" data-mp="34aohjni1b2hh">
              <div className="space-y-4" data-id="element-829" data-mp="3j2gw3xa08ci5">
                {[{
                icon: CompassIcon,
                label: 'Our Vision',
                text: 'A world where Muslim children can grow, learn, and thrive with confidence and dignity — regardless of the country they live in.'
              }, {
                icon: TargetIcon,
                label: 'Our Mission',
                text: 'To empower Muslim children and families in non-Muslim countries through high-quality education, family support, and community-based learning — while promoting integration without loss of identity.'
              }].map(b => <div key={b.label} className="rounded-2xl bg-white border border-sage-200 p-5 md:p-6 shadow-soft" data-id="element-830" data-mp="3p124dorpesq0">
                    <div className="flex items-start gap-4" data-id="element-831" data-mp="2wc4pzwqm5g2j">
                      <span className="inline-flex w-10 h-10 rounded-full bg-sage-100 text-forest-700 items-center justify-center flex-shrink-0" data-id="element-832" data-mp="31bzclo3cvjhs">
                        <b.icon size={18} strokeWidth={1.6} data-id="element-833" data-mp="36bvi5rrpbizg" />
                      </span>
                      <div data-id="element-834" data-mp="gkp55qzweotv">
                        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-forest-700 mb-1" data-id="element-835" data-mp="glif10u5lug8">
                          {b.label}
                        </p>
                        <p className="text-ink-700 leading-relaxed" data-id="element-836" data-mp="261koxk4k50kj">{b.text}</p>
                      </div>
                    </div>
                  </div>)}
                <div className="rounded-2xl bg-white border border-sage-200 p-5 md:p-6 shadow-soft" data-id="element-837" data-mp="1wckm1pmxpl12">
                  <div className="flex items-start gap-4" data-id="element-838" data-mp="1rej2l3lljjp2">
                    <span className="inline-flex w-10 h-10 rounded-full bg-sage-100 text-forest-700 items-center justify-center flex-shrink-0" data-id="element-839" data-mp="1rbww77afza8f">
                      <ShieldIcon size={18} strokeWidth={1.6} data-id="element-840" data-mp="1id1qrxf82466" />
                    </span>
                    <div data-id="element-841" data-mp="1ddovcoaaborz">
                      <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-forest-700 mb-2" data-id="element-842" data-mp="1dezzip7n6yjm">
                        Our Principles
                      </p>
                      <ul className="space-y-2 text-ink-700" data-id="element-843" data-mp="37shomgnp43gr">
                        {['Children come first', 'Identity is a strength, not a barrier', 'Education must protect as well as teach', 'Integration does not mean assimilation'].map(p => <li key={p} className="flex items-center gap-2 text-[15px]" data-id="element-844" data-mp="22vqu9qlvo759">
                            <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-845" data-mp="26upsksd3t5nq" />
                            {p}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Why Non-Muslim Countries */}
      <section id="why-non-muslim-countries" className="relative py-16 md:py-24 overflow-hidden bg-white/60" data-id="element-846" data-mp="3icivx15g0w5p">
        <Container data-id="element-847" data-mp="16p5jrrsq9c3g">
          <div className="grid lg:grid-cols-12 gap-12 items-stretch" data-id="element-848" data-mp="16mjc7s5u6wl8">
            <div className="lg:col-span-6" data-id="element-849" data-mp="12ovgimwbkzsa">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-850" data-mp="1gefbv7nkmurk">
                Why Non-Muslim
                <br data-id="element-851" data-mp="1bnqj7p05nrqu" />
                Countries
              </h2>
              <p className="mt-6 text-ink-700 leading-relaxed" data-id="element-852" data-mp="1qcv4xg65se3p">
                Muslim families living in non-Muslim countries face challenges
                that are often invisible within traditional education systems.
              </p>
              <div className="mt-5 rounded-2xl bg-white border border-sage-200 p-6" data-id="element-853" data-mp="1zci5y0uz7hw8">
                <p className="text-ink-900 font-medium mb-4" data-id="element-854" data-mp="1ue8b5rd5yjb5">
                  These challenges include, but are not limited to:
                </p>
                <ul className="space-y-3" data-id="element-855" data-mp="51mmg3i96ieo">
                  {['identity pressure and cultural isolation', 'discrimination and bullying', 'lack of culturally aware educational support', 'limited guidance for parents navigating schools and institutions'].map(c => <li key={c} className="flex items-center gap-3 text-ink-700 text-[15px]" data-id="element-856" data-mp="33e45hej7zj5v">
                      <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-857" data-mp="3v48x9y9b78h4" />
                      {c}
                    </li>)}
                </ul>
              </div>
              <p className="mt-6 text-forest-700 font-semibold" data-id="element-858" data-mp="3hdvra1fza3uf">
                International Islamic Learning Hub was created specifically to
                respond to these realities.
              </p>
              <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-859" data-mp="dw3pxwfyc7jk">
                By focusing on non-Muslim countries, we address the areas where
                Muslim children and families need structured support the most —
                helping them succeed academically, socially, and emotionally
                while remaining confident in who they are.
              </p>
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-6 relative" data-id="element-860" data-mp="z7ji7jizwrbr">
              <div className="aspect-[4/5] lg:aspect-auto lg:h-full rounded-3xl overflow-hidden shadow-card ring-4 ring-ivory" data-id="element-861" data-mp="1ix3ddr65u11b">
                <img src="https://cdn.magicpatterns.com/uploads/15TEHR9731k3AN2fQXXBYm/Non-Muslim-3.jpg" alt="Muslim boy sitting alone on a bench in a European schoolyard while other children play nearby" className="w-full h-full object-cover" data-id="element-862" data-mp="1rvfc6gh6k54u" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Governance */}
      <section id="governance" className="relative py-16 md:py-24 overflow-hidden bg-[#F7EDE9]/60" data-id="element-863" data-mp="3sxelmvufo5xx">
        <Container data-id="element-864" data-mp="bjsy004jat4k">
          <div className="grid lg:grid-cols-12 gap-12 items-stretch" data-id="element-865" data-mp="6ilre4e4nzzh">
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-5 relative" data-id="element-866" data-mp="6ibzucdfpkro">
              <div className="aspect-[4/5] lg:aspect-auto lg:h-full rounded-3xl overflow-hidden ring-4 ring-sage-100 shadow-card" data-id="element-867" data-mp="wtngiqcozneb">
                <img src="https://cdn.magicpatterns.com/uploads/1dXjjxz3sa71ZgwQ3E21Jy/Governance.jpg" alt="Muslim woman in business attire seated at a desk reviewing documents in a modern office" className="w-full h-full object-cover" data-id="element-868" data-mp="11qxckoul6xb4" />
              </div>
            </motion.div>
            <div className="lg:col-span-7" data-id="element-869" data-mp="j1qvf69fydrf">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-870" data-mp="j2iqmfz4m9h6">
                Governance &<br data-id="element-871" data-mp="53j0f3vytmiw" />
                Transparency
              </h2>
              <p className="mt-4 text-forest-700 font-semibold" data-id="element-872" data-mp="93t58d80u6w6">
                Transparency and accountability are core to our work.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-873" data-mp="23h2h2bo1i4hl">
                International Islamic Learning Hub operates with clear
                governance structures, ethical standards, and a strong
                commitment to responsible use of funds and resources.
              </p>
              <p className="mt-4 text-ink-900 font-medium" data-id="element-874" data-mp="19ph4whtu0nr4">We ensure:</p>
              <ul className="mt-3 space-y-3 text-ink-700" data-id="element-875" data-mp="14qe2z695exnd">
                {['clear separation between mission-driven and commercial activities', 'transparent reporting and accountability', 'alignment with international NGO and educational best practices'].map(c => <li key={c} className="flex items-start gap-3" data-id="element-876" data-mp="m17ro4aop8bl">
                    <CheckCircle2Icon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-877" data-mp="lzmtm4p5v78z" />
                    <span data-id="element-878" data-mp="c5e65vezm4ho">{c}</span>
                  </li>)}
              </ul>
              <p className="mt-6 text-ink-700 leading-relaxed" data-id="element-879" data-mp="842occ2cunk1">
                Our governance model is designed to meet the expectations of
                international donors, institutional partners, and the
                communities we serve.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Leadership */}
      <section id="leadership-team" className="relative py-16 md:py-24 overflow-hidden bg-sand-50/60" data-id="element-880" data-mp="2xo1jxre6vtx6">
        <Container data-id="element-881" data-mp="2035iqg7f57be">
          <div className="grid lg:grid-cols-12 gap-12 items-stretch" data-id="element-882" data-mp="1zzhofeu5ggvm">
            <div className="lg:col-span-6" data-id="element-883" data-mp="250yk8vxs1d5c">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-884" data-mp="29zs2f1o0dsbu">
                Leadership &<br data-id="element-885" data-mp="2epyr11iitcu3" />
                Team
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-886" data-mp="2esbetvaymhf2">
                Our team brings together educators, child development
                specialists, community leaders, and professionals with
                international experience.
              </p>
              <div className="mt-6 rounded-2xl bg-white border border-sage-200 p-6" data-id="element-887" data-mp="1e86ldgkdnf1u">
                <p className="font-semibold text-ink-900 mb-3" data-id="element-888" data-mp="1a8fw5hai16zf">
                  We are united by:
                </p>
                <ul className="space-y-3 text-ink-700" data-id="element-889" data-mp="1nxq50ofwzcxr">
                  {['a deep understanding of Muslim family life in non-Muslim countries', 'professional expertise in education and community work', 'a shared commitment to protecting children and supporting families'].map(c => <li key={c} className="flex items-start gap-3" data-id="element-890" data-mp="15931iaddyvma">
                      <CheckCircle2Icon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-891" data-mp="15673yn5ga8yd" />
                      <span data-id="element-892" data-mp="2bom0bvjkwq9r">{c}</span>
                    </li>)}
                </ul>
              </div>
              <div className="mt-6 rounded-2xl border border-sand-200 bg-sand-50 p-5 flex gap-4 items-start" data-id="element-893" data-mp="3tlz7oow3bt8q">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-white border border-sand-200 flex items-center justify-center p-2" data-id="element-894" data-mp="2h0ha6iuu5uk">
                  <img src="https://cdn.magicpatterns.com/uploads/hH9LkDBwayz7QC9rNa2gq8/iilh-emblem.png" alt="IILH emblem" className="w-full h-full object-contain" data-id="element-895" data-mp="3knwwjgin7k7q" />
                </div>
                <p className="text-sm text-ink-700 leading-relaxed" data-id="element-896" data-mp="l5xfumlabkil">
                  All programs are developed and delivered by qualified
                  professionals who align with our values, standards, and
                  mission.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6" data-id="element-897" data-mp="1024yhih1odvr">
              <div className="h-72 sm:h-96 lg:h-full rounded-2xl overflow-hidden shadow-card ring-4 ring-ivory" data-id="element-898" data-mp="14u64wnrj7adv">
                <img src="https://cdn.magicpatterns.com/uploads/xdGpwtJ1ncCksqBGxjJbLd/Team.jpg" alt="Three Muslim women in a warm, book-filled community room talking and smiling together" className="w-full h-full object-cover" data-id="element-899" data-mp="30y2tvjrstygt" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}